import * as THREE from 'three';
import {
  EDGE_LINE_SAMPLES,
  EDGE_NEST_SAMPLES,
  FOG_DENSITY,
  SCENE_BG,
} from '../constants';

const MAX_EDGE_SAMPLES = Math.max(48, EDGE_LINE_SAMPLES, EDGE_NEST_SAMPLES);
const GRADIENT_SCRATCH = new THREE.Color();
const FOG_COLOR = new THREE.Color(SCENE_BG);
const _lodA = new THREE.Vector3();
const _lodB = new THREE.Vector3();
const _lodC = new THREE.Vector3();
const _lodD = new THREE.Vector3();
const _lodPts = [_lodA, _lodB, _lodC, _lodD];

const LOD_BANDS = [
  { samples: 3, clip: false },
  { samples: 6, clip: false },
  { samples: 12, clip: true },
  { samples: 24, clip: true },
  { samples: 36, clip: true },
];

/** Visible NDC + slack. Faceted stubs must stay outside this, including the bow. */
const VIEW_PAD = 1.28;
/** Only then drop to the 3-sample polyline. */
const CULL_PAD = 2.4;
const MAX_BOW = 2.8;

function projectLodBounds(camera, from, to, curve) {
  _lodA.copy(from).project(camera);
  _lodB.copy(to).project(camera);
  if (curve?.v3) {
    _lodC.copy(curve.v1).project(camera);
    _lodD.copy(curve.v2).project(camera);
    return 4;
  }
  if (curve?.v0 && curve?.v1) {
    _lodC.copy(curve.v1).project(camera);
    return 3;
  }
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const bow = Math.min(MAX_BOW, Math.max(0.12 + len * 0.14, 0.45 + len * 0.08));
  const ox = (-dy / len) * bow;
  const oy = (dx / len) * bow;
  const mx = (from.x + to.x) * 0.5;
  const my = (from.y + to.y) * 0.5;
  const mz = (from.z + to.z) * 0.5;
  _lodC.set(mx + ox, my + oy, mz).project(camera);
  _lodD.set(mx - ox, my - oy, mz).project(camera);
  return 4;
}

/** True when the projected control triangle can still touch the padded frame. */
function boundsHitPad(count, pad) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let any = false;
  for (let i = 0; i < count; i += 1) {
    const p = _lodPts[i];
    if (p.z <= -1.2 || p.z >= 1.2) continue;
    any = true;
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  if (!any) return false;
  return maxX > -pad && minX < pad && maxY > -pad && minY < pad;
}

/**
 * How much curve we need: far off-screen stays a cheap polyline;
 * anything that can touch the frame keeps a smooth bow.
 */
function endpointOnScreen(p) {
  // NDC z is unstable near the near plane — a close, on-screen node can
  // report z <= -1 and get stubbed, which clips the last segment short.
  return p.x > -1.55 && p.x < 1.55 && p.y > -1.55 && p.y < 1.55 && p.z < 1.2;
}

export function filamentLod(camera, from, to, curve, forceHigh = false) {
  if (!camera) return { band: 4, ...LOD_BANDS[4] };
  const dFrom = camera.position.distanceTo(from);
  const dTo = camera.position.distanceTo(to);
  const dist = dFrom * 0.5 + dTo * 0.5;
  // Zoomed in: long stub segments clip at the near plane and never reach the disk.
  if (forceHigh || dFrom < 26 || dTo < 26 || dist < 32) {
    return { band: 4, ...LOD_BANDS[4] };
  }
  const count = projectLodBounds(camera, from, to, curve);
  const screenLen = Math.hypot(_lodA.x - _lodB.x, _lodA.y - _lodB.y);
  const endVisible = endpointOnScreen(_lodA) || endpointOnScreen(_lodB);
  if (endVisible) {
    if (screenLen > 0.22) return { band: 4, ...LOD_BANDS[4] };
    return { band: 3, ...LOD_BANDS[3] };
  }
  if (!boundsHitPad(count, CULL_PAD)) return { band: 0, ...LOD_BANDS[0] };
  if (!boundsHitPad(count, VIEW_PAD)) return { band: 1, ...LOD_BANDS[1] };
  if (dist > 34 || screenLen < 0.1) return { band: 2, ...LOD_BANDS[2] };
  if (screenLen > 0.42) return { band: 4, ...LOD_BANDS[4] };
  return { band: 3, ...LOD_BANDS[3] };
}

export function writeFilamentOccluders() {}

const LINE_VERT = /* glsl */ `
  attribute vec3 color;
  varying vec3 vColor;
  varying float vFogDepth;
  void main() {
    vColor = color;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vFogDepth = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;

const LINE_FRAG = /* glsl */ `
  uniform float uOpacity;
  uniform vec3 uTint;
  uniform vec3 uFogColor;
  uniform float uFogDensity;
  varying vec3 vColor;
  varying float vFogDepth;
  void main() {
    float fog = 1.0 - exp(-uFogDensity * uFogDensity * vFogDepth * vFogDepth);
    vec3 col = vColor * uTint;
    col = mix(col, uFogColor, clamp(fog, 0.0, 1.0));
    gl_FragColor = vec4(col, uOpacity);
  }
`;

function createLineMaterial() {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uOpacity: { value: 0 },
      uTint: { value: new THREE.Color(0xffffff) },
      uFogColor: { value: FOG_COLOR.clone() },
      uFogDensity: { value: FOG_DENSITY },
    },
    vertexShader: LINE_VERT,
    fragmentShader: LINE_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: THREE.NormalBlending,
    toneMapped: false,
    fog: false,
  });
  Object.defineProperty(mat, 'color', {
    get() { return mat.uniforms.uTint.value; },
    set(v) { mat.uniforms.uTint.value.copy(v); },
    configurable: true,
  });
  Object.defineProperty(mat, 'opacity', {
    get() { return mat.uniforms.uOpacity.value; },
    set(v) { mat.uniforms.uOpacity.value = v; },
    configurable: true,
  });
  return mat;
}

/**
 * Native 1px hairlines. Line2 (fat-line quads + custom shader) was a draw
 * call we did not need — the wires are meant to be barely there.
 */
export function createFilamentLine(color) {
  const positions = new Float32Array(MAX_EDGE_SAMPLES * 3);
  const colors = new Float32Array(MAX_EDGE_SAMPLES * 3);
  const clips = new Float32Array(MAX_EDGE_SAMPLES);
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geom.setAttribute('aClip', new THREE.BufferAttribute(clips, 1));
  geom.setDrawRange(0, EDGE_LINE_SAMPLES);

  const line = new THREE.Line(geom, createLineMaterial());
  line.frustumCulled = true;
  line.renderOrder = 0;
  line.userData.glow = null;
  line.userData.curvePositions = positions;
  line.userData.curveColors = colors;
  line.userData.curveClips = clips;
  line.userData.curvePoint = new THREE.Vector3();
  line.userData.colorKey = null;
  line.userData.sampleCount = EDGE_LINE_SAMPLES;
  line.userData.styleDirty = true;
  writeFilamentGradient(line, color, color);
  return line;
}

/** LineBasicMaterial has no resolution uniform — kept so callers stay unchanged. */
export function setFilamentResolution() {}

const ENDS_EPS2 = 1e-8;

export function filamentEndsMoved(ud, from, to) {
  const a = ud?.lastFrom;
  const b = ud?.lastTo;
  if (!a || !b) return true;
  const dx = from.x - a.x;
  const dy = from.y - a.y;
  const dz = from.z - a.z;
  const ex = to.x - b.x;
  const ey = to.y - b.y;
  const ez = to.z - b.z;
  return dx * dx + dy * dy + dz * dz > ENDS_EPS2 || ex * ex + ey * ey + ez * ez > ENDS_EPS2;
}

export function rememberFilamentEnds(ud, from, to) {
  if (!ud.lastFrom) ud.lastFrom = from.clone();
  else ud.lastFrom.copy(from);
  if (!ud.lastTo) ud.lastTo = to.clone();
  else ud.lastTo.copy(to);
}

/** Skip the sample loop when the polyline we already uploaded is still right. */
export function shouldRewriteFilament(ud, from, to, samples, forceHigh = false) {
  if (!ud) return true;
  if (filamentEndsMoved(ud, from, to)) return true;
  if (ud.sampleCount !== samples) return true;
  if (forceHigh && samples < 24) return true;
  return false;
}

export function samplesForSpan(kind, from, to, camera) {
  const lod = filamentLod(camera, from, to);
  if (kind === 'nest' || kind === 'return') {
    return Math.min(MAX_EDGE_SAMPLES, lod.samples + (lod.band >= 3 ? 4 : 0));
  }
  return lod.samples;
}

/** Sample the whole curve. Disks cover the ends — no rim chop. */
export function writeFilamentCurve(line, curve, samples = EDGE_LINE_SAMPLES, from, to) {
  const n = THREE.MathUtils.clamp(Math.round(samples), 2, MAX_EDGE_SAMPLES);
  const point = line.userData.curvePoint;
  if (line.userData.sampleCount !== n) {
    line.userData.sampleCount = n;
    line.userData.colorKey = null;
  }
  line.geometry.setDrawRange(0, n);
  const pos = line.userData.curvePositions;
  const last = n - 1;
  for (let i = 0; i <= last; i += 1) {
    curve.getPoint(i / last, point);
    pos[i * 3] = point.x;
    pos[i * 3 + 1] = point.y;
    pos[i * 3 + 2] = point.z;
  }
  if (from) {
    pos[0] = from.x;
    pos[1] = from.y;
    pos[2] = from.z;
  }
  if (to) {
    pos[last * 3] = to.x;
    pos[last * 3 + 1] = to.y;
    pos[last * 3 + 2] = to.z;
  }
  line.geometry.attributes.position.needsUpdate = true;
  const sph = line.geometry.boundingSphere || (line.geometry.boundingSphere = new THREE.Sphere());
  const ax = pos[0];
  const ay = pos[1];
  const az = pos[2];
  const bx = pos[last * 3];
  const by = pos[last * 3 + 1];
  const bz = pos[last * 3 + 2];
  sph.center.set((ax + bx) * 0.5, (ay + by) * 0.5, (az + bz) * 0.5);
  sph.radius = Math.hypot(bx - ax, by - ay, bz - az) * 0.5 + MAX_BOW + 4;
}

/** Hollow-disk clip is a shared fragment test now. Kept so callers stay unchanged. */
export function clipFilamentSamples() {}

/** Source→target colour ramp along the wire. No-op when unchanged. */
export function writeFilamentGradient(line, fromColor, toColor) {
  const n = line.userData.sampleCount || EDGE_LINE_SAMPLES;
  const key = `${fromColor.getHex()}>${toColor.getHex()}#${n}`;
  if (line.userData.colorKey === key) return;
  line.userData.colorKey = key;
  const cols = line.userData.curveColors;
  const last = n - 1;
  const c = GRADIENT_SCRATCH;
  for (let i = 0; i <= last; i += 1) {
    const t = i / last;
    c.copy(fromColor).lerp(toColor, t * t * (3 - 2 * t));
    cols[i * 3] = c.r;
    cols[i * 3 + 1] = c.g;
    cols[i * 3 + 2] = c.b;
  }
  line.geometry.attributes.color.needsUpdate = true;
}

export function disposeFilamentLine(line) {
  const glow = line.userData.glow;
  glow?.material.dispose();
  line.geometry.dispose();
  line.material.dispose();
}
